import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { z } from 'zod';
import { motion } from 'framer-motion';
import logoSymbolWhite from '@/assets/logo-symbol-white.svg';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const AdminLogin = () => {
  const { t } = useLanguage();
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      const validated = loginSchema.parse(loginForm);

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: validated.email,
        password: validated.password,
      });

      if (authError) throw authError;

      // Check for admin, super_admin, and site_admin roles
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', authData.user.id)
        .in('role', ['admin', 'super_admin', 'site_admin']);

      if (roleError) throw roleError;

      if (!roleData || roleData.length === 0) {
        await supabase.auth.signOut();
        toast({
          title: "Access Denied",
          description: "You don't have admin privileges.",
          variant: "destructive",
        });
        return;
      }

      // Determine where to redirect based on role priority
      const isSuperAdmin = roleData.some(r => r.role === 'super_admin');
      const isCompanyAdmin = roleData.some(r => r.role === 'admin');
      const isSiteAdmin = roleData.some(r => r.role === 'site_admin');

      let redirectPath = '/';
      let welcomeMsg = 'Welcome back!';

      if (isSuperAdmin) {
        redirectPath = '/platform-admin';
        welcomeMsg = 'Welcome back, Platform Admin!';
      } else if (isCompanyAdmin) {
        redirectPath = '/admin';
        welcomeMsg = 'Welcome back, Company Admin!';
      } else if (isSiteAdmin) {
        redirectPath = '/site-admin';
        welcomeMsg = 'Welcome back, Site Admin!';
      }

      toast({
        title: "Login Successful",
        description: welcomeMsg,
      });
      
      navigate(redirectPath);
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.issues.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: "Login Failed",
          description: error.message || "Invalid credentials",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-accent/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <LanguageSwitcher />
      </header>

      <main className="flex-1 flex items-center justify-center p-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="glass-card rounded-3xl p-8 shadow-medium border border-border/50">
            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4 shadow-lg shadow-primary/25">
                <img src={logoSymbolWhite} alt="SORIN" className="h-8 w-8" />
              </div>
              <h1 className="text-2xl font-bold text-center">SORIN {t('admin.title')}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t('admin.signInPrompt')}
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">{t('admin.email')}</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder={t('admin.emailPlaceholder')}
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="h-12 rounded-xl bg-background/50"
                  required
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">{t('admin.password')}</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="h-12 rounded-xl bg-background/50"
                  required
                />
                {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              </div>
              <Button type="submit" className="w-full h-12 rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:opacity-90" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('admin.signingIn')}
                  </>
                ) : (
                  t('admin.signIn')
                )}
              </Button>
            </form>

            <p className="text-xs text-muted-foreground text-center mt-6">
              Contact your administrator if you need access.
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default AdminLogin;
